import { beebeeAjaxGet } from './utility/ajax.js';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — per-browser marker "this payment was handed to a PSP and
 *              its confirmation has not reached us yet" (+ PayPal re-confirmation)
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      window.localStorage (guarded), ./utility/ajax (beebeeAjaxGet)
 *   siblings:  beebee-online-payments/components/payment-method.js (writer),
 *              beebee-base-template/controllers/pay-me.js (reader)
 *   tests:     tests/unit/lib/pending-payment-test.js
 *   gotchas:   the marker is the ONLY thing that separates "back from Stripe, webhook
 *              not landed yet" from "never paid": Stripe's success_url and cancel_url
 *              for pay_me are the same URL (api create_session, POP-431), and the payment row
 *              stays success=false until checkout.session.completed lands. Without
 *              the marker the pay page re-offered the methods and the patient could
 *              pay twice. Storage can be missing or throw (private mode, blocked
 *              site data): every access is guarded and a missing marker degrades to
 *              the old behaviour, never to a crash.
 */
const KEY_PREFIX = 'bb-pending-payment:';

// A redirect-based PSP (Stripe, Satispay, bank POS) confirms within seconds; a day
// is long enough to cover a delayed webhook and short enough that a marker left by
// an abandoned checkout does not block a later, genuine attempt for long.
const PENDING_PAYMENT_TTL_MS = 24 * 60 * 60 * 1000;

// Methods that leave the page and come back to the SAME url whether the payer paid
// or cancelled: the page cannot tell which, so the payer is allowed to say so.
const REDIRECT_METHODS = Object.freeze(['stripe', 'satispay', 'pos_virtuale']);
function defaultStorage() {
  try {
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}
function isBlankId(paymentId) {
  return paymentId === undefined || paymentId === null || paymentId === '';
}
function keyFor(paymentId) {
  return `${KEY_PREFIX}${String(paymentId)}`;
}

/**
 * Remember that `paymentId` was handed to a PSP. `info` carries the method, the
 * user the request was addressed to (`requestBy`) and, for PayPal, the captured
 * order so the confirmation can be replayed.
 * @returns {boolean} true when the marker was written
 */
function markPaymentPending(paymentId, info = {}, {
  storage = defaultStorage(),
  now = Date.now()
} = {}) {
  if (isBlankId(paymentId) || !storage) {
    return false;
  }
  try {
    storage.setItem(keyFor(paymentId), JSON.stringify({
      ...info,
      at: now
    }));
    return true;
  } catch {
    return false;
  }
}

/**
 * The live marker for `paymentId`, or null (none, corrupt, or older than the TTL —
 * the last two are removed on the way).
 */
function readPendingPayment(paymentId, {
  storage = defaultStorage(),
  now = Date.now()
} = {}) {
  if (isBlankId(paymentId) || !storage) {
    return null;
  }
  let raw;
  try {
    raw = storage.getItem(keyFor(paymentId));
  } catch {
    return null;
  }
  if (!raw) {
    return null;
  }
  let info = null;
  try {
    info = JSON.parse(raw);
  } catch {
    info = null;
  }
  const at = Number(info?.at);
  if (!info || typeof info !== 'object' || !Number.isFinite(at) || now - at > PENDING_PAYMENT_TTL_MS) {
    clearPendingPayment(paymentId, {
      storage
    });
    return null;
  }
  return info;
}
function clearPendingPayment(paymentId, {
  storage = defaultStorage()
} = {}) {
  if (isBlankId(paymentId) || !storage) {
    return;
  }
  try {
    storage.removeItem(keyFor(paymentId));
  } catch {
    // storage unavailable: nothing to clear
  }
}

/**
 * Tell the API that a PayPal order captured in the browser settled `paymentId`.
 * PayPal has no webhook here: this call IS the confirmation, so when it fails the
 * money is taken and the payment still reads unpaid until the call is replayed.
 */
function confirmPaypalCapture(urlApi, token, {
  paymentId,
  orderId,
  createTime,
  booking
} = {}) {
  return beebeeAjaxGet(`${urlApi}/payment_request_success`, token, {
    id: paymentId,
    success: true,
    payment_method: 'paypal',
    payment_id: orderId,
    date: createTime,
    booking
  });
}

export { PENDING_PAYMENT_TTL_MS, REDIRECT_METHODS, clearPendingPayment, confirmPaypalCapture, markPaymentPending, readPendingPayment };
//# sourceMappingURL=pending-payment.js.map
